import { useApi, useLoad } from "@allergy123/api-client/react";
import { Button, Card, Status } from "@allergy123/ui";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { classOf, classPath, enrollmentFor, isComplete, minutesLabel, modulePath, positionOf } from "../lib";

/**
 * The class player: video with captions, transcript, resume position saved
 * every few seconds and on pause, completion recorded when the video ends or
 * the learner marks it, then the next class or the knowledge check.
 */
export function ClassPage() {
  const { code = "" } = useParams();
  const api = useApi();
  const data = useLoad(async (a) => {
    const [cat, learning] = await Promise.all([a.catalog(), a.learning.me()]);
    const found = classOf(cat, code);
    const enrollment = found ? enrollmentFor(learning.enrollments, found.module.code) : undefined;
    const hasVideo = found?.cls.media.includes("video");
    const [video, captions, transcript] = enrollment && hasVideo
      ? await Promise.all([
          a.media(found!.cls.code, "video"),
          found!.cls.media.includes("captions") ? a.media(found!.cls.code, "captions") : null,
          found!.cls.media.includes("transcript") ? a.media(found!.cls.code, "transcript").then((m) => fetch(m.url).then((r) => r.text())) : null,
        ])
      : [null, null, null];
    return { cat, found, enrollment, video, captions, transcript };
  }, [code]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [renewedUrl, setRenewedUrl] = useState<string | null>(null);
  const renewing = useRef(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [completed, setCompleted] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const lastSaved = useRef(0);

  const enrollment = data.data?.enrollment;
  const cls = data.data?.found?.cls;

  useEffect(() => {
    if (data.data?.enrollment && cls) setCompleted(isComplete(data.data.enrollment, cls.code) ? "already" : null);
  }, [data.data, cls]);

  // Resume where the learner left off once the media is ready.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !enrollment || !cls) return;
    const pos = positionOf(enrollment, cls.code);
    const seek = () => {
      if (pos > 0 && pos < v.duration - 5) v.currentTime = pos;
    };
    v.addEventListener("loadedmetadata", seek, { once: true });
    return () => v.removeEventListener("loadedmetadata", seek);
  }, [enrollment, cls, data.data?.video?.url]);

  async function savePosition(force = false) {
    const v = videoRef.current;
    if (!v || !cls || !enrollment) return;
    const t = Math.floor(v.currentTime);
    if (!force && Math.abs(t - lastSaved.current) < 10) return;
    lastSaved.current = t;
    await api.learning.progress(cls.code, t).catch(() => undefined);
  }

  /** The media link is short-lived; when it expires mid-class the element errors, so fetch a new one and carry on from the same second. */
  async function renewVideo() {
    const v = videoRef.current;
    if (!v || !cls || renewing.current) return;
    renewing.current = true;
    const at = v.currentTime;
    const wasPlaying = !v.paused;
    try {
      const fresh = await api.media(cls.code, "video");
      setRenewedUrl(fresh.url);
      v.addEventListener(
        "loadedmetadata",
        () => {
          v.currentTime = at;
          if (wasPlaying) void v.play();
        },
        { once: true },
      );
    } catch {
      /* the element keeps its error state; the learner can reload */
    } finally {
      renewing.current = false;
    }
  }

  async function complete() {
    if (!cls || saving) return;
    setSaving(true);
    try {
      const res = await api.learning.complete(cls.code);
      setCompleted(res.created ? "now" : "already");
    } finally {
      setSaving(false);
    }
  }

  if (data.state === "error") return <Status kind="error" label={`Couldn't load this class (${data.error?.message})`} />;
  if (!data.data) return <p className="text-ink-secondary">Loading…</p>;
  const { cat, found, video, captions, transcript } = data.data;
  if (!found || !cls) return <Status kind="error" label="This class isn't in the catalog." />;
  const { module } = found;
  const idx = module.classes.findIndex((k) => k.code === cls.code);
  const nextClass = module.classes[idx + 1];

  return (
    <>
      <p className="mb-2 text-small text-ink-secondary">
        <Link to="/training">Training</Link> · <Link to={modulePath(cat, module.code)}>{module.title}</Link>
      </p>
      <h1 className="mb-1 text-h1 font-semibold text-anchor">{cls.title}</h1>
      <p className="mb-6 text-small text-ink-secondary">
        {cls.code} · {minutesLabel(cls.estimatedMinutes)}
      </p>

      {!enrollment ? (
        <Card tone="tinted">
          <p className="mb-4">Enroll in {module.title} to open this class.</p>
          <Button href={modulePath(cat, module.code)}>Go to the module</Button>
        </Card>
      ) : video ? (
        <>
          {/* Video sits on the blue-indigo ground whatever the page's ground (02_colors.md v3.0.0, Grounds by medium). */}
          <div data-theme="dark" className="overflow-hidden rounded-md border border-rule-strong bg-canvas">
            {/* crossOrigin lets the captions track load from the media origin; the link is short-lived and scoped to this learner. */}
            <video
              ref={videoRef}
              className="aspect-video w-full"
              controls
              playsInline
              preload="metadata"
              crossOrigin="anonymous"
              src={renewedUrl ?? video.url}
              onError={() => void renewVideo()}
              onTimeUpdate={() => void savePosition()}
              onPause={() => void savePosition(true)}
              onEnded={() => {
                void savePosition(true);
                void complete();
              }}
            >
              {captions && <track kind="captions" srcLang="en" label="English" src={captions.url} default />}
            </video>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            {completed ? (
              <Status kind="success" label={completed === "now" ? "Completion recorded" : "Completed earlier"} />
            ) : (
              <Button variant="secondary" onClick={() => void complete()} disabled={saving}>
                Mark as complete
              </Button>
            )}
            {transcript !== null && (
              <Button variant="secondary" onClick={() => setShowTranscript((s) => !s)} aria-expanded={showTranscript}>
                {showTranscript ? "Hide transcript" : "Show transcript"}
              </Button>
            )}
            {nextClass && (
              <Button href={classPath(nextClass.code)} className="ml-auto">
                Next: {nextClass.code}
              </Button>
            )}
            {!nextClass && cls.checkCode && cls.checkAuthored && (
              <Button href={`/training/checks/${cls.checkCode}`} className="ml-auto">
                Take the knowledge check
              </Button>
            )}
            {!nextClass && cls.checkCode && !cls.checkAuthored && <Status kind="info" label="Check in production" className="ml-auto" />}
          </div>
          {showTranscript && transcript && (
            <Card className="mt-6 whitespace-pre-wrap text-body" as="section" aria-label="Transcript">
              {transcript.replace(/^# .*\n\n/, "").replace(/^## /gm, "")}
            </Card>
          )}
        </>
      ) : (
        <Card>
          <h2 className="mb-2 text-h3 font-semibold text-anchor">Video in production</h2>
          <p className="mb-4 max-w-[68ch]">This class is mapped but its lesson hasn't been published yet. What it will cover:</p>
          <ul className="mb-6 list-disc pl-6">
            {cls.topics.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-4">
            {nextClass && <Button variant="secondary" surface="card" href={classPath(nextClass.code)}>Next: {nextClass.code}</Button>}
            <Button variant="secondary" surface="card" href={modulePath(cat, module.code)}>
              Back to the module
            </Button>
          </div>
        </Card>
      )}

      {enrollment && video && cls.topics.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-2 text-h3 font-semibold text-anchor">What this class covers</h2>
          <ul className="list-disc pl-6 text-ink-secondary">
            {cls.topics.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
